import React, { useCallback } from 'react';
import { Map as MapIcon } from 'lucide-react';
import { toast } from 'react-toastify';

import { Button } from '@/components/ui/button';
import { useOptionalUscgMapContext } from 'components/MapPage/CorporateViewTabs/LayersTab/UscgMapContext';
import { performZoomToAor } from 'components/MapPage/CorporateViewTabs/AorsTab/utils/useAutoZoomToHomeAor';
import { useAors } from 'hooks/useAors';

import { nextAorFilterIds } from './childAorMapFilter';
import { useAorText } from 'utils/aorTerminology';

type ChildAorMapButtonProps = {
  aorId: string | null | undefined;
  aorName: string;
};

/**
 * Folded-map icon button for a subordinate AOR section: shows the map, zooms to
 * the AOR's polygon and opens the AOR popup (which renders the AOR's latest
 * published report).
 */
export default function ChildAorMapButton({
  aorId,
  aorName,
}: ChildAorMapButtonProps) {
  const aorText = useAorText();
  const mapCtx = useOptionalUscgMapContext();
  const { data: aors } = useAors();

  const aor = aorId ? aors?.find((item) => item.id === aorId) : undefined;
  const hasGeometry = !!aor?.geolocation?.geojson?.data;

  const onClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      if (!aorId) return;
      if (!mapCtx || !mapCtx.mapView) {
        mapCtx?.setIsMapVisible(true);
        toast.info(aorText('Open the map to view this AOR'));
        return;
      }
      if (!hasGeometry) {
        toast.info(`No map area is defined for ${aorName}`);
        return;
      }
      mapCtx.setIsMapVisible(true);
      void performZoomToAor(aorId, mapCtx.mapView, aors, {
        ensureVisible: true,
        filterIds: nextAorFilterIds(mapCtx.aorFilterIds, aorId),
        currentAorFilterIds: mapCtx.aorFilterIds,
        isAorVisible: mapCtx.isAorVisible,
        onFilterChange: mapCtx.setAorFilterIds,
        onVisibilityChange: mapCtx.setIsAorVisible,
      }).catch((error) => {
        console.error('Failed to zoom to subordinate AOR:', error);
        toast.error(`Could not show ${aorName} on the map.`);
      });
    },
    [aorId, aorName, aorText, aors, hasGeometry, mapCtx]
  );

  if (!aorId) return null;

  return (
    <Button
      type="button"
      variant="ghost"
      size="icon-sm"
      title={
        aors && !hasGeometry
          ? 'No map area defined'
          : `Zoom map to ${aorName} and open its report`
      }
      aria-label={`Show ${aorName} on map`}
      disabled={!!aors && !hasGeometry}
      onClick={onClick}
      data-testid="child-aor-map-button"
    >
      <MapIcon className="tw:size-4" />
    </Button>
  );
}
